import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * Hook for sticky sections (always pinned).
 * Sections are always sticky — no toggle needed.
 *
 * @param {string} pageKey  - unique page identifier (e.g. 'crm', 'devices')
 * @param {string[]} sections - ordered section keys (e.g. ['toolbar', 'stats'])
 * @returns {{ pins, togglePin, sectionRef, getStickyStyle, totalPinnedHeight }}
 */
export default function useStickyPin(pageKey, sections = ['toolbar']) {
  // All sections always pinned
  const pins = useMemo(() => {
    const p = {};
    sections.forEach(s => { p[s] = true; });
    return p;
  }, [sections]);

  const refs = useRef({});
  const [heights, setHeights] = useState({});
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  // Measure heights of pinned sections using ResizeObserver
  useEffect(() => {
    const measure = () => {
      const newHeights = {};
      for (const key of sectionsRef.current) {
        const el = refs.current[key];
        if (el) newHeights[key] = el.offsetHeight;
      }
      setHeights(prev => {
        const changed = sectionsRef.current.some(k => prev[k] !== newHeights[k]);
        return changed ? newHeights : prev;
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    for (const key of sectionsRef.current) {
      const el = refs.current[key];
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  // No-op, kept for backward compatibility
  const togglePin = useCallback(() => {}, []);

  const sectionRef = useCallback((section) => (el) => {
    refs.current[section] = el;
  }, []);

  const getStickyStyle = useCallback((section) => {
    const idx = sections.indexOf(section);
    let top = 0;
    for (let i = 0; i < idx; i++) {
      top += heights[sections[i]] || 0;
    }
    return {
      position: 'sticky',
      top: `${top}px`,
      zIndex: 100 - idx,
    };
  }, [sections, heights]);

  const totalPinnedHeight = sections.reduce((sum, s) => {
    return sum + (heights[s] || 0);
  }, 0);

  return { pins, togglePin, sectionRef, getStickyStyle, totalPinnedHeight };
}
